/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { ArrowRight, ArrowUp, Check, CircleNotch, Crown, Fire } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn, type PlanTier, TIER_RANK } from "@reactive-resume/utils";
import { isAxiosError } from "axios";
import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
import { trackTikTokInitiateCheckout } from "@/client/libs/analytics";
import { axios } from "@/client/libs/axios";
import { messageFromServerError } from "@/client/services/errors/translate-error";
import { useAuthStore } from "@/client/stores/auth";
import { useBillingStore } from "@/client/stores/billing";

type PlanFeature = {
  text: string;
  badge?: string;
  badgeColor?: string;
  included: boolean;
};

type Plan = {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  annualTotal: number;
  freeMonths: string;
  description: string;
  popular?: boolean;
  features: PlanFeature[];
  extraIntro?: string;
};

const plans: Plan[] = [
  {
    name: "Starter",
    monthlyPrice: 9,
    annualPrice: 6,
    annualTotal: 69,
    freeMonths: "~4 months free",
    description: "Get started with AI-powered resume building and land your first interviews",
    features: [
      { text: "1 resume", included: true },
      { text: "5 professional templates", included: true },
      { text: "30 AI actions per day", included: true },
      { text: "AI writing: improve, fix grammar, change tone", included: true, badge: "AI Basic", badgeColor: "bg-blue-500/10 text-blue-600" },
      { text: "PDF export", included: true },
      { text: "A4 + Letter formats", included: true },
      { text: "5 standard fonts", included: true },
    ],
  },
  {
    name: "Pro",
    monthlyPrice: 19,
    annualPrice: 12,
    annualTotal: 139,
    freeMonths: "~4 months free",
    description: "Full AI toolkit for serious job seekers: write, score, match, and export",
    popular: true,
    extraIntro: "All Starter features, plus:",
    features: [
      { text: "3 resumes", included: true },
      { text: "All 12+ templates", included: true },
      { text: "150 AI actions per day", included: true },
      { text: "Full AI writing: generate, expand, quantify", included: true, badge: "AI Full", badgeColor: "bg-blue-500/10 text-blue-600" },
      { text: "ATS Score Checker (0-100)", included: true, badge: "ATS Optimizer", badgeColor: "bg-green-500/10 text-green-600" },
      { text: "Job description keyword matching (5/mo)", included: true },
      { text: "Cover letters (3/mo)", included: true },
      { text: "PDF + DOCX export", included: true },
      { text: "All Google Fonts + full color customization", included: true },
      { text: "LinkedIn data import", included: true },
      { text: "Resume sharing + view tracking", included: true },
    ],
  },
  {
    name: "Career Max",
    monthlyPrice: 39,
    annualPrice: 22,
    annualTotal: 259,
    freeMonths: "~5 months free",
    description: "Unlimited everything for active job seekers who want every advantage",
    extraIntro: "All Pro features, plus:",
    features: [
      { text: "Unlimited resumes", included: true },
      { text: "Unlimited AI actions", included: true },
      { text: "Auto-tailoring to job descriptions", included: true, badge: "Smart Tailoring", badgeColor: "bg-purple-500/10 text-purple-600" },
      { text: "Unlimited cover letters", included: true },
      { text: "Unlimited job description matching", included: true },
      { text: "Full resume review with detailed feedback", included: true },
      { text: "AI chat assistant", included: true },
      { text: "Priority AI processing", included: true },
      { text: "Multi-page resume support", included: true },
      { text: "Advanced analytics (views, downloads)", included: true },
    ],
  },
  {
    name: "Ultra",
    monthlyPrice: 79,
    annualPrice: 44,
    annualTotal: 529,
    freeMonths: "~5 months free",
    description: "Maximum power with priority processing and enterprise-level features",
    extraIntro: "All Career Max features, plus:",
    features: [
      { text: "Fastest AI processing (priority queue)", included: true },
      { text: "Bulk resume operations", included: true },
      { text: "JSON data export/backup", included: true },
      { text: "Early access to new features", included: true, badge: "Early Access", badgeColor: "bg-orange-500/10 text-orange-600" },
      { text: "Priority email support", included: true },
    ],
  },
];

const tierKeys = ["starter", "pro", "careerMax", "ultra"] as const;


const SubscribeButton = ({
  tier,
  annual,
  popular,
}: {
  tier: NonNullable<PlanTier>;
  annual: boolean;
  popular?: boolean;
}) => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = !!user;
  const userTier = user?.planTier ?? null;
  const openPaywall = useBillingStore((s) => s.openPaywall);
  const [loading, setLoading] = useState(false);

  const userRank = userTier ? (TIER_RANK[userTier] ?? 0) : 0;
  const planRank = TIER_RANK[tier] ?? 0;
  const isCurrent = isLoggedIn && userTier && planRank === userRank;
  const isLower = isLoggedIn && userTier && planRank < userRank;
  const isHigher = isLoggedIn && userTier && planRank > userRank;

  const handleClick = async () => {
    if (!isLoggedIn) {
      const interval = annual ? "annual" : "monthly";
      navigate(`/auth/register?plan=${tier}&interval=${interval}`);
      return;
    }

    if (isHigher) {
      openPaywall(undefined, `Upgrade to unlock more features`);
      return;
    }

    setLoading(true);
    try {
      const interval = annual ? "annual" : "monthly";
      const { data } = await axios.post<{ url: string; sessionId: string }>("/billing/checkout", {
        tier,
        interval,
        sourceUrl: window.location.href,
      });
      if (data.sessionId) {
        await trackTikTokInitiateCheckout({
          tier,
          interval,
          eventId: data.sessionId,
          userId: user?.id ?? null,
          email: user?.email,
        });
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }

      toast({
        variant: "error",
        title: t`We couldn't open checkout. Please try again in a moment.`,
      });
      setLoading(false);
    } catch (error) {
      if (!isAxiosError(error) || !messageFromServerError(error.response?.data)) {
        toast({
          variant: "error",
          title: t`We couldn't open checkout. Please try again in a moment.`,
        });
      }
      setLoading(false);
    }
  };

  if (isCurrent) {
    return (
      <Button className="mt-6 w-full" variant="ghost" disabled>
        <Crown size={16} weight="fill" className="mr-1.5" />
        {t`Current Plan`}
      </Button>
    );
  }

  if (isLower) {
    return (
      <Button className="mt-6 w-full" variant="ghost" disabled>
        {t`Included`}
      </Button>
    );
  }

  return (
    <Button
      className="mt-6 w-full"
      variant={popular ? "primary" : "outline"}
      disabled={loading}
      onClick={handleClick}
    >
      {loading ? (
        <CircleNotch size={16} className="animate-spin" />
      ) : isHigher ? (
        <>
          {t`Upgrade`}
          <ArrowUp className="ml-1.5" weight="bold" />
        </>
      ) : (
        <>
          {t`Start 3-day trial`}
          <ArrowRight className="ml-1.5" weight="bold" />
        </>
      )}
    </Button>
  );
};

export const PricingSection = () => {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="relative bg-secondary-accent/50 py-24 sm:py-32">
      <div className="container">
        <motion.div
          className="mx-auto max-w-2xl text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            {t`Pricing`}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            {t`Choose the plan that fits your job search`}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/60">
            {t`Start with a 3-day free trial. Card required, cancel anytime. All plans include professional templates, AI-powered features, and ATS-optimized exports.`}
          </p>
        </motion.div>

        <div className="mt-6 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-green-500/10 px-4 py-1.5 text-xs font-medium text-green-600">
            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {t`3-day free trial · 30-day money-back guarantee on all plans`}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <div className="inline-flex rounded-full border border-border/50 bg-background p-1">
            <button
              onClick={() => setAnnual(false)}
              className={cn(
                "rounded-full px-4 py-2 text-sm font-medium transition-colors sm:px-5",
                !annual
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground/60 hover:bg-secondary-accent hover:text-foreground",
              )}
            >
              {t`Monthly`}
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors sm:px-5",
                annual
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground/60 hover:bg-secondary-accent hover:text-foreground",
              )}
            >
              <Fire weight="fill" className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{t`Yearly: save up to 5 months`}</span>
              <span className="sm:hidden">{t`Yearly: save 5 months`}</span>
            </button>
          </div>
        </div>

        <div className="mx-auto mt-12 grid max-w-7xl gap-6 lg:grid-cols-4">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
              className={cn(
                "relative flex flex-col rounded-2xl border bg-background p-6",
                plan.popular
                  ? "border-primary shadow-lg shadow-primary/10"
                  : "border-border/50",
              )}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-primary-foreground">
                  {t`Most popular`}
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold">{plan.name}</h3>

                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight">
                    ${annual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-sm text-foreground/50">{t`per month`}</span>
                </div>

                {annual && (
                  <p className="mt-1 text-xs text-foreground/50">
                    {t`billed annually`} ${plan.annualTotal} &middot;{" "}
                    <span className="font-medium text-green-600">{plan.freeMonths}</span>
                  </p>
                )}

                <p className="mt-4 text-sm leading-relaxed text-foreground/60">
                  {plan.description}
                </p>
              </div>

              <SubscribeButton
                tier={tierKeys[index] ?? plan.name.toLowerCase().replace(/\s+/g, "")}
                annual={annual}
                popular={plan.popular}
              />

              {annual && (
                <a
                  href="#pricing"
                  onClick={(e) => { e.preventDefault(); setAnnual(false); }}
                  className="mt-2 text-center text-xs text-foreground/40 hover:text-foreground/60"
                >
                  {t`View monthly billing`} ↗
                </a>
              )}

              <div className="mt-6 border-t border-border/30 pt-6">
                {plan.extraIntro && (
                  <p className="mb-3 text-xs font-medium text-foreground/50">
                    ← {plan.extraIntro}
                  </p>
                )}
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check weight="bold" className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                      <span className="text-foreground/70">
                        {feature.badge && (
                          <span
                            className={cn(
                              "mr-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold",
                              feature.badgeColor,
                            )}
                          >
                            {feature.badge}
                          </span>
                        )}
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
